import { useEffect, useRef, useCallback } from 'react';
import { Chart } from 'chart.js';
import { createDonutChart } from './util/DonutChart';
import { RatioEvents } from '@/types/AnalysisResultType';
import chartIcon from '@/assets/images/sidebar/analysis_icon.png';
import { useTranslation } from 'react-i18next';
import { getHoursAndMinutes } from '@/utils/time';
type DonutChartProps = {
    data: RatioEvents;
    onSwitchToStacked?: () => void;
    hideSwitchButton?: boolean;
};

/**
 * ドーナツチャートコンポーネント
 * @param data - データ
 * @returns ドーナツチャート
 */
const DonutChart = ({ data, onSwitchToStacked, hideSwitchButton }: DonutChartProps) => {
    // フック
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const chartRef = useRef<Chart<'doughnut'> | null>(null);
    const { t } = useTranslation('analysis');
    const { t: tCommon } = useTranslation('common');

    const formatDuration = useCallback(
        (minutes: number) => {
            const [hours, mins] = getHoursAndMinutes(minutes);
            const parts: string[] = [];
            if (hours > 0) {
                parts.push(tCommon('time.hours_one', { count: hours }));
            }
            if (mins > 0 || parts.length === 0) {
                parts.push(tCommon('time.minutes_one', { count: mins }));
            }
            return parts.join('');
        },
        [tCommon],
    );

    // マウント時
    useEffect(() => {
        let isMounted = true;

        (async () => {
            // データが空の場合はチャートを作成しない
            if (data.ratioEventItems.length === 0) return;

            // 既存のチャートがあれば先に破棄
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }

            if (!canvasRef.current) return;

            // コンテキストを取得
            const ctx = canvasRef.current.getContext('2d');
            if (!ctx) return;

            // 新しくチャートを作成（非同期）
            const chart = await createDonutChart(ctx, data, { formatDuration });
            if (!isMounted) {
                // アンマウント済みなら即破棄
                chart?.destroy();
                return;
            }
            chartRef.current = chart ?? null;
        })();

        // アンマウント時
        return () => {
            isMounted = false;
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [data, formatDuration]);

    // レンダリング
    return (
        <div className="w-full h-full flex flex-col">
            {data.ratioEventItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                    <img src={chartIcon} className="w-20 h-20 image-protection" />
                    <p>{t('chart.noData')}</p>
                </div>
            ) : (
                <>
                    <div className="flex justify-end px-2 pt-2 pb-1">
                        {!hideSwitchButton && (
                            <button
                                type="button"
                                aria-label="switch-to-stacked"
                                className="px-2 py-1 text-xs rounded bg-white/80 hover:bg-white shadow border border-gray-200"
                                onClick={() => onSwitchToStacked?.()}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <rect x="3" y="14" width="4" height="7" fill="#4B5563" />
                                    <rect x="10" y="10" width="4" height="11" fill="#9CA3AF" />
                                    <rect x="17" y="6" width="4" height="15" fill="#D1D5DB" />
                                </svg>
                            </button>
                        )}
                    </div>
                    <div className="relative flex-1 min-h-0">
                        <canvas ref={canvasRef} className="w-full h-full" />
                    </div>
                </>
            )}
        </div>
    );
};

export default DonutChart;
